import React, { useEffect, useState } from 'react';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { Box, Grid, Typography } from '@mui/material';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { EPerPages, ISortOptions } from 'models/table';
import { ILicencesFirearmsSearchForm } from 'models/licencesFirearms';
import { Pagination } from 'components/molecules/Pagination';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import LicenceStore from 'state/LicenceStore';
import { isPathEqualToRouteName } from 'utils/route';
import { FormModal } from 'components/molecules/Modal';
import { CardList } from 'components/molecules/CardList';
import { FirearmCard } from './FirearmCard';
import { useLicencesFirearms } from './hooks';
import { firearmsTableColumns, mapFirearmsToDataSource } from './helpers';
import { FirearmsSearchForm } from './FirearmsSearchForm';
import { useLegalEntities } from '../hooks';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';

const values: ILicencesFirearmsSearchForm = {
  legalUniqueId: '',
  fireCycleId: '',
  type: '',
  make: '',
  model: '',
  action: '',
  calibre: '',
  serialNumber: '',
  deletedUniqueIds: [],
};

const LegalEntityLicenceLinkWeaponPage = () => {
  const { t } = useTranslations();
  const { state: routerState, pathname }: any = useLocation();
  const { isDesktop } = useThemeBreakpoint();
  const { clearAlert, samePageAlert } = useAlert();
  const { getScheduledLicenceData } = useLegalEntities();
  const { getLegalEntityLicenceDetailsData, legalEntityLicenceDetails } = useLegalEntities();
  const navigate = useNavigate();
  const query = useQuery();
  const { id = '', licenceId = '' } = useParams<{
    id: string;
    licenceId: string;
  }>();
  const type: string | null = query.get('type')?.trim() || null;
  const expand: string | null = query.get('expand');
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [checkedWeapon, setCheckedWeapon] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions>();
  const [initValues, setInitValues] = useState<ILicencesFirearmsSearchForm>(values);
  const [readLimitsActual, setReadLimitsActual] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const { selectedLanguage } = useLang();
  const {
    getMake,
    make,
    getModel,
    model,
    getFirearmAction,
    firearmAction,
    getFirearmType,
    firearmType,
    getCaliber,
    caliber,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getMake();
      getModel();
      getFirearmAction();
      getCaliber();
      getFirearmType();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const {
    getLicencesSearchData,
    licencesFirearms,
    paginator,
    handleWeaponLicenceFirearmsToLink,
    readLimits,
    getReadLimits,
  } = useLicencesFirearms();

  const effectiveWeaponLimit = routerState?.weaponNumberLimit ?? readLimits?.max ?? 0;

  useEffect(() => {
    (async () => {
      if (!legalEntityLicenceDetails) {
        await getLegalEntityLicenceDetailsData(licenceId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function init() {
      getReadLimits(licenceId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenceId]);

  useEffect(() => {
    const limitActualFromState = routerState?.countCurrent ?? null;
    if (limitActualFromState !== null) {
      setReadLimitsActual(limitActualFromState);
    } else if (readLimits) {
      setReadLimitsActual(readLimits?.actual);
    }
  }, [readLimits]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (id && licenceId) {
      setIsLoading(true);

      const weaponsToLink = LicenceStore.getFormWeapon(licenceId);
      const weaponsToLinkIds = weaponsToLink?.map((weapon: any) => weapon.uniqueId) || [];

      const finalDeletedUniqueIds = weaponsToLinkIds;

      setInitValues({
        ...values,
        legalUniqueId: id,
        deletedUniqueIds: finalDeletedUniqueIds,
      });
      setIsLoading(false);
    }
  }, [id, licenceId]);

  useEffect(() => {
    (async function init() {
      if (initValues.legalUniqueId) {
        const isFromVaryRenew = isPathEqualToRouteName(
          pathname,
          ERouteLinks.LegalEntityLicenceLinkWeaponVaryRenew,
        );
        await getLicencesSearchData(
          currentPage,
          perPage,
          initValues,
          sort,
          licenceId,
          isFromVaryRenew,
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, initValues, sort, licenceId]);

  const handleSearch = (searchFormData: any) => {
    setInitValues({
      ...searchFormData,
      legalUniqueId: id,
      deletedUniqueIds: initValues.deletedUniqueIds,
    });
  };

  const handleCheck = (firearmId: string, checkedItems: any) => {
    if (checkedIds.includes(firearmId)) {
      setCheckedIds((currentCheckedIds) =>
        currentCheckedIds.filter((item) => item !== firearmId),
      );
      setCheckedWeapon((currentChecked) =>
        currentChecked.filter((item) => item.uniqueId !== firearmId),
      );
      setReadLimitsActual(readLimitsActual - 1);
    } else if (readLimitsActual < effectiveWeaponLimit || effectiveWeaponLimit === 0) {
      setCheckedIds((currentCheckedIds) => [...currentCheckedIds, firearmId]);
      setCheckedWeapon((currentChecked) => [...currentChecked, checkedItems]);
      setReadLimitsActual(readLimitsActual + 1);
    }
  };

  const handleSuccessLink = () => {
    const targetUrl = routerState?.prevLink
      ? routerState?.prevLink
      : `${ERouteLinks.LegalEntityViewLicence.replace(':id', id as string).replace(
          ':licenceId',
          licenceId,
        )}?type=${
          type === ELegalEntityTypes.individual
            ? LEAccordionNames.INDIVIDUAL
            : LEAccordionNames.ORGANISATION
        }& expand=${expand} `;

    navigate(targetUrl, {
      state: routerState?.saveToStory ? { saveToStory: true } : undefined,
    });
  };

  const handleLinkWeapon = async () => {
    if (checkedIds.length && licenceId) {
      let success = false;
      let shouldUpdateScheduled = true;
      let cannotUpdateScheduled = false;

      const limitSchedule = await getScheduledLicenceData(
        legalEntityLicenceDetails?.general?.licenceNumber ?? '',
      );

      if (
        limitSchedule === null ||
        (limitSchedule?.weaponNumberLimit &&
          checkedIds.length + limitSchedule.actualWeaponAmount >
            limitSchedule.weaponNumberLimit)
      ) {
        shouldUpdateScheduled = false;
      }

      if (routerState?.saveToStory) {
        LicenceStore.setFormWeapon(checkedWeapon, licenceId);
        success = true;
      } else if (
        !routerState?.saveToStory &&
        (await handleWeaponLicenceFirearmsToLink(checkedIds, licenceId, shouldUpdateScheduled))
      ) {
        cannotUpdateScheduled = limitSchedule !== null && !shouldUpdateScheduled;
        setShowModal(cannotUpdateScheduled);
        success = true;
      }

      if (success && !cannotUpdateScheduled) {
        handleSuccessLink();
      }
    }
  };

  return (
    <>
      <FormModal
        open={showModal}
        onClickClose={() => {
          setShowModal(false);
          handleSuccessLink();
        }}
        title={t('legalEntity.licenceDetails.scheduled.modal.title')}
        text={t('weaponLicenceFirearmsToLink.save.successWithoutScheduled.text')}
        cancelButtonLabel="general.ok.label"
        titleLeft
      />
      <Breadcrumbs
        items={
          routerState?.breadcrumb
            ? /* eslint-disable-next-line no-unsafe-optional-chaining */
              [...routerState?.breadcrumb, t('legalEntity.licenceDetails.linkWeapon.header')]
            : [
                { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
                {
                  label:
                    type === ELegalEntityTypes.individual
                      ? t('legalEntities.individual.accordion.label')
                      : t('legalEntities.organization.accordion.label'),
                  route: `${ERouteLinks.LegalEntities}?expand=${
                    type === ELegalEntityTypes.individual
                      ? LEAccordionNames.INDIVIDUAL
                      : LEAccordionNames.ORGANISATION
                  } `,
                },
                {
                  label: t('legalEntityDetails.legalEntityDetails.text'),
                  route: `${ERouteLinks.ViewLegalEntity.replace(':id', id as string)}?type=${
                    type === ELegalEntityTypes.individual
                      ? LEAccordionNames.INDIVIDUAL
                      : LEAccordionNames.ORGANISATION
                  }& expand=${expand} `,
                },
                {
                  label: t('legalEntityDetails.view.licence.label'),
                  route: `${ERouteLinks.LegalEntityViewLicence.replace(
                    ':id',
                    id as string,
                  ).replace(':licenceId', licenceId)}?type=${
                    type === ELegalEntityTypes.individual
                      ? LEAccordionNames.INDIVIDUAL
                      : LEAccordionNames.ORGANISATION
                  }& expand=${expand} `,
                },
                t('legalEntity.licenceDetails.linkWeapon.header'),
              ]
        }
      />
      <StyledPageTitle variant="h4">
        {t('legalEntity.licenceDetails.linkWeapon.header')}
      </StyledPageTitle>
      <Grid container sx={{ rowGap: 10, mt: 2 }}>
        <Grid container size={{ xs: 12 }} sx={{ rowGap: 2.5 }}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body1">
              {t('legalEntity.licenceDetails.linkWeapon.subtitle')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box>
              <Alert
                text={t('legalEntity.licenceDetails.linkWeapon.alert')}
                variant={EAlertVariants.warning}
                small
              />
            </Box>
          </Grid>

          <Grid sx={{ gap: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2">
                {t('legalEntity.licenceDetails.weapon.weaponLimitNumber.text')}
              </Typography>
            </Grid>
            {readLimits && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2">{`${readLimitsActual}/${
                  effectiveWeaponLimit > 0
                    ? effectiveWeaponLimit
                    : t('legalEntity.licenceDetails.weapon.unlimited.text')
                }`}</Typography>
              </Grid>
            )}
          </Grid>

          {type === ELegalEntityTypes.organisation && (
            <Grid size={{ xs: 12 }}>
              <FirearmsSearchForm onSubmit={handleSearch} />
            </Grid>
          )}

          {samePageAlert && (
            <Grid size={{ xs: 12 }}>
              <Box>
                <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
              </Box>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            {isLoading || !licencesFirearms ? (
              <Loader isLoading={true} />
            ) : (
              <>
                {isDesktop ? (
                  <Table
                    columns={firearmsTableColumns({
                      handleCheck,
                      checkedIds,
                    })}
                    dataSource={mapFirearmsToDataSource(
                      licencesFirearms,
                      firearmType,
                      make,
                      model,
                      caliber,
                      firearmAction,
                    )}
                    onSort={setSort}
                  />
                ) : (
                  <CardList
                    items={mapFirearmsToDataSource(
                      licencesFirearms,
                      firearmType,
                      make,
                      model,
                      caliber,
                      firearmAction,
                    )}
                    render={(data, index) => (
                      <FirearmCard
                        data={data}
                        key={index}
                        handleCheck={handleCheck}
                        checkedIds={checkedIds}
                      />
                    )}
                  />
                )}

                {paginator && (
                  <Pagination
                    count={paginator?.totalElements}
                    perPage={perPage}
                    onChangePage={(page) => {
                      setCurrentPage(page);
                    }}
                    onChangePerPage={(value) => {
                      setPerPage(value);
                    }}
                    current={currentPage}
                    isLoading={isLoading}
                  />
                )}
              </>
            )}
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12, lg: 9, xl: 6 }} columnSpacing={2} sx={{ rowGap: 1 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              onClick={handleLinkWeapon}
              fullWidth
              label={t('legalEntity.licenceDetails.linkWeapon.button')}
              id="save-button"
              disabled={isLoading || !checkedIds.length}
              variant={EButtonVariants.contained}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              fullWidth
              label={t('general.cancel.button')}
              id="cancel-button"
              variant={EButtonVariants.outlined}
              onClick={() => {
                const targetUrl = routerState?.prevLink
                  ? routerState?.prevLink
                  : `${ERouteLinks.LegalEntityViewLicence.replace(':id', id as string).replace(
                      ':licenceId',
                      licenceId,
                    )}?type=${
                      type === ELegalEntityTypes.individual
                        ? LEAccordionNames.INDIVIDUAL
                        : LEAccordionNames.ORGANISATION
                    }& expand=${expand} `;

                navigate(targetUrl, {
                  state: routerState?.saveToStory ? { saveToStory: true } : undefined,
                });
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export { LegalEntityLicenceLinkWeaponPage };
